import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../../context/auth-context.jsx';
import { adminApi } from '../../../lib/admin-api/index.js';
import {
  isInvalidSecurityStepUpCodeError,
  isSecurityStepUpRequiredError,
  isSensitiveActionRestrictedError,
} from '../../../lib/admin-api/http-client.js';
import { buildSyncSuccessMessage } from '../../accounts/dto/accounts-list.dto.js';
import { mapPartnershipResponseToDto } from '../dto/profile-partnership.dto.js';
import { normalizeSteps } from '../../../pages/system/kyc-shared.js';
import { mapProfileResponseToDto } from '../dto/profile-details.dto.js';
import { buildProfileDetailsModel } from '../model/profile-details.model.js';
import {
  buildProfileEditForm,
  buildProfileUpdatePayload,
  validateEmailChangeForm,
} from '../model/profile-edit.model.js';
import { shouldLoadProfileSuitability } from '../model/profile-details-compliance.model.js';
import { buildProfileDetailsTabs, PROFILE_DETAILS_TABS } from '../model/profile-details-tabs.model.js';
import { mapInternalMailListResponse } from '../../internal-mail/dto/internal-mail.dto.js';
import { useComplianceMode } from '../../system-navigation/context/compliance-mode-context.jsx';
import { buildTransactionsRequestDto, mapTransactionsResponseToDto } from '../../transactions/dto/transactions-list.dto.js';
import { buildTransactionsTableModel } from '../../transactions/model/transactions-list.model.js';

function pickFirst(...values) {
  return values.find((value) => value !== undefined && value !== null && String(value).trim() !== '');
}

function normalizeList(value) {
  if (Array.isArray(value)) {
    return value;
  }

  if (value && typeof value === 'object') {
    return Object.values(value);
  }

  return [];
}

function normalizeAccounts(payload) {
  if (Array.isArray(payload?.accounts)) {
    return payload.accounts;
  }
  if (payload?.accounts && typeof payload.accounts === 'object') {
    return Object.values(payload.accounts);
  }
  return normalizeList(payload);
}

function normalizeDocuments(payload) {
  if (Array.isArray(payload?.documents)) {
    return payload.documents;
  }
  if (payload?.documents && typeof payload.documents === 'object') {
    return Object.values(payload.documents);
  }
  return normalizeList(payload);
}

function normalizeKyc(payload) {
  const candidates = [
    payload?.steps,
    payload?.items,
    payload?.data?.steps,
    payload?.data?.items,
    payload,
  ];

  for (const candidate of candidates) {
    const list = normalizeList(candidate);
    if (list.length) {
      return list;
    }
  }

  return [];
}

function normalizeActionLogs(payload) {
  const candidates = [
    payload?.actionLogs,
    payload?.items,
    payload?.data?.actionLogs,
    payload?.data?.items,
    payload,
  ];

  for (const candidate of candidates) {
    const list = normalizeList(candidate).filter((item) => item && typeof item === 'object');
    if (list.length) {
      return list;
    }
  }

  return [];
}

function normalizeSuitabilityAssessments(payload) {
  const candidates = [
    payload?.items,
    payload?.assessments,
    payload?.data?.items,
    payload?.data?.assessments,
    payload,
  ];

  for (const candidate of candidates) {
    const list = normalizeList(candidate).filter((item) => item && typeof item === 'object');
    if (list.length) {
      return list;
    }
  }

  return [];
}

function buildPartnerDisplay(partner) {
  if (!partner || typeof partner !== 'object') {
    return '';
  }

  const name = pickFirst(partner.name, partner.fullName, partner.full_name) || '-';
  const email = pickFirst(partner.email) || '-';
  return `${name} <${email}>`;
}

function normalizePartnerSearchResults(payload) {
  const candidates = [
    payload?.partners,
    payload?.items,
    payload?.data?.partners,
    payload?.data?.items,
    payload,
  ];

  for (const candidate of candidates) {
    const list = normalizeList(candidate).filter((item) => item && typeof item === 'object');
    if (list.length) {
      return list.map((partner, index) => ({
        uid: String(pickFirst(partner.uid, partner.id, `partner-${index}`) || '').trim(),
        name: pickFirst(partner.name) || '-',
        email: pickFirst(partner.email) || '-',
        display: pickFirst(partner.display) || buildPartnerDisplay(partner),
      }));
    }
  }

  return [];
}

function extractJurisdictionId(profileSource) {
  return (
    pickFirst(
      profileSource?.jurisdictionId,
      profileSource?.jurisdiction_id,
      profileSource?.jurisdiction?.uid,
      profileSource?.jurisdiction?.id,
    ) || ''
  );
}

function formatExportFileName(profileId) {
  const normalizedProfileId = String(profileId || 'profile').trim() || 'profile';
  const timestamp = new Date().toISOString().replaceAll(':', '-');
  return `profile-export-${normalizedProfileId}-${timestamp}.json`;
}

function downloadProfileExportPayload(profileId, response) {
  const payload = response?.data ?? response ?? {};
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = formatExportFileName(profileId);
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}

const PROFILE_UPDATE_PERMISSION_ERROR = 'You do not have permission to update this profile.';
const PROFILE_PII_VIEW_PERMISSION_ERROR = 'You do not have permission to reveal this profile email.';
const PROFILE_PHONE_PII_VIEW_PERMISSION_ERROR = 'You do not have permission to reveal this profile phone.';
const SENSITIVE_ACTION_RESTRICTED_MESSAGE = 'Sensitive action restricted';
const INVALID_SECURITY_STEP_UP_CODE_MESSAGE = 'Invalid security step-up code';
const PROFILE_TRANSACTIONS_PAGE_SIZE = 20;
const PROFILE_TRANSACTION_STATUS_OPTIONS = [
  'created',
  'pending',
  'processed',
  'failed',
  'succeed',
  'canceled',
  'resolution',
  'pending_input',
];
const EMPTY_PROFILE_TRANSACTION_FILTERS = Object.freeze({
  type: '',
  method: '',
  status: '',
});
const DEFAULT_PROFILE_TRANSACTION_SORT = Object.freeze({
  sortBy: 'created',
  sortOrder: 'desc',
});

function normalizeProfileTransactionFilters(filters) {
  return {
    type: String(filters?.type || '').trim().toLowerCase(),
    method: String(filters?.method || '').trim(),
    status: String(filters?.status || '').trim().toLowerCase(),
  };
}

function createSecurityRequestId() {
  if (typeof window !== 'undefined' && typeof window.crypto?.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }

  return `admin-step-up-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function useProfileDetails() {
  const { token, hasPermission } = useAuth();
  const canViewExternalVerifications = typeof hasPermission === 'function' ? hasPermission('kyc.view') : false;
  const canUpdateProfile = typeof hasPermission === 'function' ? hasPermission('profile.update') : false;
  const canRevealProfilePii = typeof hasPermission === 'function' ? hasPermission('profile.pii.view') : false;
  const { id } = useParams();
  const currentProfileId = String(id || '').trim();
  const mountedRef = useRef(false);
  const currentProfileIdRef = useRef(currentProfileId);
  const profileEditSaveRequestRef = useRef(0);
  const securityStepUpActionRef = useRef(null);
  currentProfileIdRef.current = currentProfileId;
  const { isFinancialRegulation } = useComplianceMode();
  const [activeTab, setActiveTab] = useState(PROFILE_DETAILS_TABS.profile);
  const [profile, setProfile] = useState(null);
  const [profileEditForm, setProfileEditForm] = useState(() => buildProfileEditForm(null));
  const [profileEditOriginal, setProfileEditOriginal] = useState(() => buildProfileEditForm(null));
  const [profileEditErrors, setProfileEditErrors] = useState({});
  const [isProfileSaving, setIsProfileSaving] = useState(false);
  const [profileSaveError, setProfileSaveError] = useState('');
  const [profileSaveSuccess, setProfileSaveSuccess] = useState('');
  const [profileEmailErrors, setProfileEmailErrors] = useState({});
  const [isProfileEmailSaving, setIsProfileEmailSaving] = useState(false);
  const [profileEmailRevealErrors, setProfileEmailRevealErrors] = useState({});
  const [isProfileEmailRevealSaving, setIsProfileEmailRevealSaving] = useState(false);
  const [revealedEmail, setRevealedEmail] = useState('');
  const [profilePhoneRevealErrors, setProfilePhoneRevealErrors] = useState({});
  const [isProfilePhoneRevealSaving, setIsProfilePhoneRevealSaving] = useState(false);
  const [revealedPhone, setRevealedPhone] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [isAccountsLoading, setIsAccountsLoading] = useState(false);
  const [accountsError, setAccountsError] = useState('');
  const [accountsLoaded, setAccountsLoaded] = useState(false);
  const [partnership, setPartnership] = useState({
    historyPartners: [],
    accounts: [],
  });
  const [isPartnershipLoading, setIsPartnershipLoading] = useState(false);
  const [partnershipError, setPartnershipError] = useState('');
  const [partnershipLoaded, setPartnershipLoaded] = useState(false);
  const [isPartnershipModalOpen, setIsPartnershipModalOpen] = useState(false);
  const [partnershipModalAccount, setPartnershipModalAccount] = useState(null);
  const [partnershipPartnerQuery, setPartnershipPartnerQuery] = useState('');
  const [partnershipSearchTouched, setPartnershipSearchTouched] = useState(false);
  const [partnershipSearchResults, setPartnershipSearchResults] = useState([]);
  const [isPartnershipSearchLoading, setIsPartnershipSearchLoading] = useState(false);
  const [partnershipSearchError, setPartnershipSearchError] = useState('');
  const [selectedPartnershipPartner, setSelectedPartnershipPartner] = useState(null);
  const [isPartnershipSaving, setIsPartnershipSaving] = useState(false);
  const [partnershipSaveError, setPartnershipSaveError] = useState('');
  const [syncingAccountId, setSyncingAccountId] = useState('');
  const [accountSyncError, setAccountSyncError] = useState('');
  const [accountSyncSuccess, setAccountSyncSuccess] = useState('');
  const [kyc, setKyc] = useState([]);
  const [isKycLoading, setIsKycLoading] = useState(false);
  const [kycError, setKycError] = useState('');
  const [kycLoaded, setKycLoaded] = useState(false);
  const [kycSettings, setKycSettings] = useState([]);
  const [isKycSettingsLoading, setIsKycSettingsLoading] = useState(false);
  const [kycSettingsError, setKycSettingsError] = useState('');
  const [kycSettingsLoaded, setKycSettingsLoaded] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [isDocumentsLoading, setIsDocumentsLoading] = useState(false);
  const [documentsError, setDocumentsError] = useState('');
  const [documentsLoaded, setDocumentsLoaded] = useState(false);
  const [actionLogs, setActionLogs] = useState([]);
  const [isActionLogsLoading, setIsActionLogsLoading] = useState(false);
  const [actionLogsError, setActionLogsError] = useState('');
  const [actionLogsLoaded, setActionLogsLoaded] = useState(false);
  const [profileMail, setProfileMail] = useState([]);
  const [isProfileMailLoading, setIsProfileMailLoading] = useState(false);
  const [profileMailError, setProfileMailError] = useState('');
  const [profileMailLoaded, setProfileMailLoaded] = useState(false);
  const [isVerifyingProfile, setIsVerifyingProfile] = useState(false);
  const [isRequestingInformation, setIsRequestingInformation] = useState(false);
  const [isInvalidatingProfile, setIsInvalidatingProfile] = useState(false);
  const [isValidatingProfile, setIsValidatingProfile] = useState(false);
  const [verifyProfileError, setVerifyProfileError] = useState('');
  const [verifyProfileSuccess, setVerifyProfileSuccess] = useState('');
  const [isAnonymizingProfile, setIsAnonymizingProfile] = useState(false);
  const [isExportingProfile, setIsExportingProfile] = useState(false);
  const [complianceError, setComplianceError] = useState('');
  const [complianceSuccess, setComplianceSuccess] = useState('');
  const [suitabilityAssessments, setSuitabilityAssessments] = useState([]);
  const [isSuitabilityLoading, setIsSuitabilityLoading] = useState(false);
  const [suitabilityError, setSuitabilityError] = useState('');
  const [suitabilityLoaded, setSuitabilityLoaded] = useState(false);
  const [isCreatingSuitability, setIsCreatingSuitability] = useState(false);
  const [isUpdatingVulnerability, setIsUpdatingVulnerability] = useState(false);
  const [auditIntegrity, setAuditIntegrity] = useState(null);
  const [isAuditChecking, setIsAuditChecking] = useState(false);
  const [trades, setTrades] = useState([]);
  const [tradesTotal, setTradesTotal] = useState(0);
  const [tradesTotalLots, setTradesTotalLots] = useState('0');
  const [tradesTotalProfit, setTradesTotalProfit] = useState('0');
  const [tradesProfitCurrency, setTradesProfitCurrency] = useState('USD');
  const [isTradesLoading, setIsTradesLoading] = useState(false);
  const [tradesError, setTradesError] = useState('');
  const [tradesOffset, setTradesOffset] = useState(0);
  const [tradesSymbolFilter, setTradesSymbolFilter] = useState('');
  const [tradesDateFromFilter, setTradesDateFromFilter] = useState('');
  const [tradesDateToFilter, setTradesDateToFilter] = useState('');
  const [profileTransactionsRows, setProfileTransactionsRows] = useState([]);
  const [isProfileTransactionsLoading, setIsProfileTransactionsLoading] = useState(false);
  const [isProfileTransactionsLoadingMore, setIsProfileTransactionsLoadingMore] = useState(false);
  const [hasMoreProfileTransactions, setHasMoreProfileTransactions] = useState(false);
  const [profileTransactionsError, setProfileTransactionsError] = useState('');
  const [profileTransactionsLoaded, setProfileTransactionsLoaded] = useState(false);
  const [profileTransactionFilters, setProfileTransactionFilters] = useState(EMPTY_PROFILE_TRANSACTION_FILTERS);
  const [appliedProfileTransactionFilters, setAppliedProfileTransactionFilters] = useState(EMPTY_PROFILE_TRANSACTION_FILTERS);
  const [profileTransactionSort, setProfileTransactionSort] = useState(DEFAULT_PROFILE_TRANSACTION_SORT);
  const [securityStepUp, setSecurityStepUp] = useState({
    isOpen: false,
    isSubmitting: false,
    error: '',
  });

  const applyProfileResponse = useCallback((response) => {
    const dto = mapProfileResponseToDto(response);
    const model = buildProfileDetailsModel(dto);
    const editForm = buildProfileEditForm(model);

    setProfile(model);
    setProfileEditForm(editForm);
    setProfileEditOriginal(editForm);
    setRevealedEmail('');
    setRevealedPhone('');

    return model;
  }, []);

  const beginProfileEditSaveRequest = useCallback(() => {
    profileEditSaveRequestRef.current += 1;
    return profileEditSaveRequestRef.current;
  }, []);

  const isCurrentProfileEditSaveRequest = useCallback((requestId, profileId) => (
    mountedRef.current
    && profileEditSaveRequestRef.current === requestId
    && currentProfileIdRef.current === profileId
  ), []);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      profileEditSaveRequestRef.current += 1;
    };
  }, []);

  useEffect(() => {
    profileEditSaveRequestRef.current += 1;
    securityStepUpActionRef.current = null;
    setSecurityStepUp({
      isOpen: false,
      isSubmitting: false,
      error: '',
    });
    setActiveTab(PROFILE_DETAILS_TABS.profile);
    setProfileEditForm(buildProfileEditForm(null));
    setProfileEditOriginal(buildProfileEditForm(null));
    setProfileEditErrors({});
    setIsProfileSaving(false);
    setProfileSaveError('');
    setProfileSaveSuccess('');
    setProfileEmailErrors({});
    setIsProfileEmailSaving(false);
    setProfileEmailRevealErrors({});
    setIsProfileEmailRevealSaving(false);
    setRevealedEmail('');
    setProfilePhoneRevealErrors({});
    setIsProfilePhoneRevealSaving(false);
    setRevealedPhone('');
    setAccounts([]);
    setAccountsLoaded(false);
    setAccountsError('');
    setPartnership({ historyPartners: [], accounts: [] });
    setPartnershipLoaded(false);
    setPartnershipError('');
    setIsPartnershipLoading(false);
    setIsPartnershipModalOpen(false);
    setPartnershipModalAccount(null);
    setPartnershipPartnerQuery('');
    setPartnershipSearchTouched(false);
    setPartnershipSearchResults([]);
    setIsPartnershipSearchLoading(false);
    setPartnershipSearchError('');
    setSelectedPartnershipPartner(null);
    setIsPartnershipSaving(false);
    setPartnershipSaveError('');
    setSyncingAccountId('');
    setAccountSyncError('');
    setAccountSyncSuccess('');
    setKyc([]);
    setKycLoaded(false);
    setKycError('');
    setKycSettings([]);
    setKycSettingsLoaded(false);
    setIsKycSettingsLoading(false);
    setKycSettingsError('');
    setDocuments([]);
    setDocumentsLoaded(false);
    setDocumentsError('');
    setActionLogs([]);
    setActionLogsLoaded(false);
    setActionLogsError('');
    setProfileMail([]);
    setProfileMailLoaded(false);
    setProfileMailError('');
    setIsVerifyingProfile(false);
    setIsRequestingInformation(false);
    setVerifyProfileError('');
    setVerifyProfileSuccess('');
    setIsAnonymizingProfile(false);
    setIsExportingProfile(false);
    setComplianceError('');
    setComplianceSuccess('');
    setSuitabilityAssessments([]);
    setIsSuitabilityLoading(false);
    setSuitabilityError('');
    setSuitabilityLoaded(false);
    setIsCreatingSuitability(false);
    setIsUpdatingVulnerability(false);
    setAuditIntegrity(null);
    setIsAuditChecking(false);
    setTrades([]);
    setTradesTotal(0);
    setTradesTotalLots('0');
    setTradesTotalProfit('0');
    setTradesProfitCurrency('USD');
    setIsTradesLoading(false);
    setTradesError('');
    setTradesOffset(0);
    setTradesSymbolFilter('');
    setTradesDateFromFilter('');
    setTradesDateToFilter('');
    setProfileTransactionsRows([]);
    setIsProfileTransactionsLoading(false);
    setIsProfileTransactionsLoadingMore(false);
    setHasMoreProfileTransactions(false);
    setProfileTransactionsError('');
    setProfileTransactionsLoaded(false);
    setProfileTransactionFilters(EMPTY_PROFILE_TRANSACTION_FILTERS);
    setAppliedProfileTransactionFilters(EMPTY_PROFILE_TRANSACTION_FILTERS);
    setProfileTransactionSort(DEFAULT_PROFILE_TRANSACTION_SORT);
  }, [id]);

  const openSecurityStepUp = useCallback((action) => {
    securityStepUpActionRef.current = action;
    setSecurityStepUp({
      isOpen: true,
      isSubmitting: false,
      error: '',
    });
  }, []);

  const cancelSecurityStepUp = useCallback(() => {
    securityStepUpActionRef.current = null;
    setSecurityStepUp({
      isOpen: false,
      isSubmitting: false,
      error: '',
    });
  }, []);

  const submitSecurityStepUpCode = useCallback(async (code) => {
    const action = securityStepUpActionRef.current;
    const normalizedCode = String(code || '').trim();
    if (!action) {
      return false;
    }
    if (!normalizedCode) {
      setSecurityStepUp((prev) => ({
        ...prev,
        error: 'Security code is required.',
      }));
      return false;
    }

    setSecurityStepUp((prev) => ({
      ...prev,
      isSubmitting: true,
      error: '',
    }));

    try {
      await adminApi.verifySecurityStepUp(token, { code: normalizedCode }, {
        requestId: action.requestId,
      });
      const result = await action.retry();
      securityStepUpActionRef.current = null;
      setSecurityStepUp({
        isOpen: false,
        isSubmitting: false,
        error: '',
      });
      return result;
    } catch (requestError) {
      const errorMessage = isInvalidSecurityStepUpCodeError(requestError)
        ? INVALID_SECURITY_STEP_UP_CODE_MESSAGE
        : requestError.message || 'Failed to verify security step-up';
      setSecurityStepUp((prev) => ({
        ...prev,
        isSubmitting: false,
        error: errorMessage,
      }));
      return false;
    }
  }, [token]);

  useEffect(() => {
    let isCancelled = false;

    if (!id) {
      setProfile(null);
      setProfileEditForm(buildProfileEditForm(null));
      setProfileEditOriginal(buildProfileEditForm(null));
      setError('Profile id is required');
      setIsLoading(false);
      return () => {
        isCancelled = true;
      };
    }

    const guardedLoad = async () => {
      setIsLoading(true);
      setError('');

      try {
        const response = await adminApi.getProfile(token, id);
        if (!isCancelled) {
          applyProfileResponse(response);
        }
      } catch (requestError) {
        if (!isCancelled) {
          setProfile(null);
          setProfileEditForm(buildProfileEditForm(null));
          setProfileEditOriginal(buildProfileEditForm(null));
          setError(requestError.message || 'Failed to load profile details');
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    guardedLoad();

    return () => {
      isCancelled = true;
    };
  }, [applyProfileResponse, id, token]);

  useEffect(() => {
    let isCancelled = false;

    if (activeTab !== PROFILE_DETAILS_TABS.accounts || accountsLoaded || !id) {
      return undefined;
    }

    const loadAccounts = async () => {
      setIsAccountsLoading(true);
      setAccountsError('');
      try {
        const response = await adminApi.getProfileAccounts(token, id);
        if (!isCancelled) {
          setAccounts(normalizeAccounts(response));
          setAccountsLoaded(true);
        }
      } catch (requestError) {
        if (!isCancelled) {
          setAccounts([]);
          setAccountsError(requestError.message || 'Failed to load profile accounts');
        }
      } finally {
        if (!isCancelled) {
          setIsAccountsLoading(false);
        }
      }
    };

    loadAccounts();

    return () => {
      isCancelled = true;
    };
  }, [activeTab, accountsLoaded, id, token]);

  useEffect(() => {
    let isCancelled = false;

    if (activeTab !== PROFILE_DETAILS_TABS.partnership || partnershipLoaded || !id) {
      return undefined;
    }

    const loadPartnership = async () => {
      setIsPartnershipLoading(true);
      setPartnershipError('');

      try {
        const response = await adminApi.getProfilePartnership(token, id);
        if (!isCancelled) {
          setPartnership(mapPartnershipResponseToDto(response));
          setPartnershipLoaded(true);
        }
      } catch (requestError) {
        if (!isCancelled) {
          setPartnership({ historyPartners: [], accounts: [] });
          setPartnershipError(requestError.message || 'Failed to load profile partnership');
        }
      } finally {
        if (!isCancelled) {
          setIsPartnershipLoading(false);
        }
      }
    };

    loadPartnership();

    return () => {
      isCancelled = true;
    };
  }, [activeTab, partnershipLoaded, id, token]);

  useEffect(() => {
    let isCancelled = false;

    if (!isPartnershipModalOpen || !partnershipSearchTouched) {
      return undefined;
    }

    const query = String(partnershipPartnerQuery || '').trim();
    if (query.length < 2) {
      setPartnershipSearchResults([]);
      setPartnershipSearchError('');
      setIsPartnershipSearchLoading(false);
      return undefined;
    }

    const loadPartnershipSearch = async () => {
      setIsPartnershipSearchLoading(true);
      setPartnershipSearchError('');

      try {
        const response = await adminApi.searchPartners(token, query, { limit: 10 });
        if (!isCancelled) {
          setPartnershipSearchResults(normalizePartnerSearchResults(response));
        }
      } catch (requestError) {
        if (!isCancelled) {
          setPartnershipSearchResults([]);
          setPartnershipSearchError(requestError.message || 'Failed to search partners');
        }
      } finally {
        if (!isCancelled) {
          setIsPartnershipSearchLoading(false);
        }
      }
    };

    loadPartnershipSearch();

    return () => {
      isCancelled = true;
    };
  }, [isPartnershipModalOpen, partnershipPartnerQuery, partnershipSearchTouched, token]);

  useEffect(() => {
    let isCancelled = false;

    if (![PROFILE_DETAILS_TABS.kyc, PROFILE_DETAILS_TABS.verification].includes(activeTab) || kycLoaded || !id) {
      return undefined;
    }

    const loadKyc = async () => {
      setIsKycLoading(true);
      setKycError('');
      try {
        const response = await adminApi.getProfileKyc(token, id);
        if (!isCancelled) {
          setKyc(normalizeKyc(response));
          setKycLoaded(true);
        }
      } catch (requestError) {
        if (!isCancelled) {
          setKyc([]);
          setKycError(requestError.message || 'Failed to load profile KYC');
        }
      } finally {
        if (!isCancelled) {
          setIsKycLoading(false);
        }
      }
    };

    loadKyc();

    return () => {
      isCancelled = true;
    };
  }, [activeTab, id, kycLoaded, token]);

  useEffect(() => {
    let isCancelled = false;

    if (![PROFILE_DETAILS_TABS.kyc, PROFILE_DETAILS_TABS.verification].includes(activeTab) || !kycLoaded || kycSettingsLoaded) {
      return undefined;
    }

    const jurisdictionId = extractJurisdictionId(profile?.source);
    if (!jurisdictionId) {
      setKycSettings([]);
      setKycSettingsLoaded(true);
      return undefined;
    }

    const loadKycSettings = async () => {
      setIsKycSettingsLoading(true);
      setKycSettingsError('');
      try {
        const response = await adminApi.getKycSettings(token, jurisdictionId);
        if (!isCancelled) {
          setKycSettings(normalizeSteps(response));
          setKycSettingsLoaded(true);
        }
      } catch (requestError) {
        if (!isCancelled) {
          setKycSettings([]);
          setKycSettingsLoaded(true);
          setKycSettingsError(requestError.message || 'Failed to load KYC settings');
        }
      } finally {
        if (!isCancelled) {
          setIsKycSettingsLoading(false);
        }
      }
    };

    loadKycSettings();

    return () => {
      isCancelled = true;
    };
  }, [activeTab, kycLoaded, kycSettingsLoaded, profile, token]);

  const refreshProfileDocuments = useCallback(async ({ silent = false } = {}) => {
    const profileId = currentProfileId;
    if (!profileId) {
      return [];
    }

    if (!silent) {
      setIsDocumentsLoading(true);
    }
    setDocumentsError('');

    try {
      const response = await adminApi.getProfileDocuments(token, profileId);
      const nextDocuments = normalizeDocuments(response);

      if (mountedRef.current && currentProfileIdRef.current === profileId) {
        setDocuments(nextDocuments);
        setDocumentsLoaded(true);
      }

      return nextDocuments;
    } catch (requestError) {
      if (mountedRef.current && currentProfileIdRef.current === profileId) {
        if (!silent) {
          setDocuments([]);
        }
        setDocumentsError(requestError.message || 'Failed to load profile documents');
      }
      throw requestError;
    } finally {
      if (!silent && mountedRef.current && currentProfileIdRef.current === profileId) {
        setIsDocumentsLoading(false);
      }
    }
  }, [currentProfileId, token]);

  useEffect(() => {
    if (![PROFILE_DETAILS_TABS.documents, PROFILE_DETAILS_TABS.verification].includes(activeTab) || documentsLoaded || !id) {
      return undefined;
    }

    refreshProfileDocuments().catch(() => {});
    return undefined;
  }, [activeTab, documentsLoaded, id, refreshProfileDocuments]);

  useEffect(() => {
    let isCancelled = false;

    if (activeTab !== PROFILE_DETAILS_TABS.actionLog || actionLogsLoaded || !id) {
      return undefined;
    }

    const loadActionLogs = async () => {
      setIsActionLogsLoading(true);
      setActionLogsError('');
      try {
        const response = await adminApi.getProfileActionLog(token, id, {
          limit: 100,
          offset: 0,
          order: 'desc',
        });
        if (!isCancelled) {
          setActionLogs(normalizeActionLogs(response));
          setActionLogsLoaded(true);
        }
      } catch (requestError) {
        if (!isCancelled) {
          setActionLogs([]);
          setActionLogsError(requestError.message || 'Failed to load action log');
        }
      } finally {
        if (!isCancelled) {
          setIsActionLogsLoading(false);
        }
      }
    };

    loadActionLogs();

    return () => {
      isCancelled = true;
    };
  }, [activeTab, actionLogsLoaded, id, token]);

  useEffect(() => {
    let isCancelled = false;

    if (activeTab !== PROFILE_DETAILS_TABS.internalMail || profileMailLoaded || !id) {
      return undefined;
    }

    const loadProfileMail = async () => {
      setIsProfileMailLoading(true);
      setProfileMailError('');
      try {
        const response = await adminApi.getProfileInternalMailList(token, id);
        if (!isCancelled) {
          setProfileMail(mapInternalMailListResponse(response));
          setProfileMailLoaded(true);
        }
      } catch (requestError) {
        if (!isCancelled) {
          setProfileMail([]);
          setProfileMailError(requestError.message || 'Failed to load internal mail');
        }
      } finally {
        if (!isCancelled) {
          setIsProfileMailLoading(false);
        }
      }
    };

    loadProfileMail();

    return () => {
      isCancelled = true;
    };
  }, [activeTab, profileMailLoaded, id, token]);

  useEffect(() => {
    let isCancelled = false;

    if (!shouldLoadProfileSuitability({
      activeTab,
      isFinancialRegulation,
      profileId: id,
      suitabilityLoaded,
    })) {
      return undefined;
    }

    const loadSuitability = async () => {
      setIsSuitabilityLoading(true);
      setSuitabilityError('');

      try {
        const response = await adminApi.getProfileSuitability(token, id);
        if (!isCancelled) {
          setSuitabilityAssessments(normalizeSuitabilityAssessments(response));
          setSuitabilityLoaded(true);
        }
      } catch (requestError) {
        if (!isCancelled) {
          setSuitabilityAssessments([]);
          setSuitabilityLoaded(true);
          setSuitabilityError(requestError.message || 'Failed to load suitability history');
        }
      } finally {
        if (!isCancelled) {
          setIsSuitabilityLoading(false);
        }
      }
    };

    loadSuitability();

    return () => {
      isCancelled = true;
    };
  }, [activeTab, id, isFinancialRegulation, suitabilityLoaded, token]);

  useEffect(() => {
    let isCancelled = false;

    if (activeTab !== PROFILE_DETAILS_TABS.trades || !id) {
      return undefined;
    }

    const loadTrades = async () => {
      setIsTradesLoading(true);
      setTradesError('');
      try {
        const filters = {
          limit: 50,
          offset: tradesOffset,
        };
        if (tradesSymbolFilter) filters.symbol = tradesSymbolFilter;
        if (tradesDateFromFilter) filters.dateFrom = tradesDateFromFilter;
        if (tradesDateToFilter) filters.dateTo = tradesDateToFilter;

        const response = await adminApi.getProfileTrades(token, id, filters);
        if (!isCancelled) {
          const newTrades = Array.isArray(response?.trades) ? response.trades : [];
          setTrades((prev) => tradesOffset === 0 ? newTrades : [...prev, ...newTrades]);
          setTradesTotal(response?.total ?? 0);
          setTradesTotalLots(response?.totalLots ?? '0');
          setTradesTotalProfit(response?.totalProfit ?? '0');
          setTradesProfitCurrency(response?.profitCurrency ?? 'USD');
        }
      } catch (requestError) {
        if (!isCancelled) {
          if (tradesOffset === 0) setTrades([]);
          setTradesError(requestError.message || 'Failed to load trades');
        }
      } finally {
        if (!isCancelled) {
          setIsTradesLoading(false);
        }
      }
    };

    loadTrades();

    return () => {
      isCancelled = true;
    };
  }, [activeTab, id, token, tradesOffset, tradesSymbolFilter, tradesDateFromFilter, tradesDateToFilter]);

  const handleTradesSymbolChange = useCallback((value) => {
    setTradesOffset(0);
    setTradesSymbolFilter(value);
  }, []);

  const handleTradesDateFromChange = useCallback((value) => {
    setTradesOffset(0);
    setTradesDateFromFilter(value);
  }, []);

  const handleTradesDateToChange = useCallback((value) => {
    setTradesOffset(0);
    setTradesDateToFilter(value);
  }, []);

  const loadMoreTrades = useCallback(() => {
    setTradesOffset((prev) => prev + trades.length);
  }, [trades.length]);

  const loadProfileTransactions = useCallback(async ({ offset = 0, append = false } = {}) => {
    const profileId = currentProfileId;
    if (!profileId) {
      return;
    }

    if (append) {
      setIsProfileTransactionsLoadingMore(true);
    } else {
      setIsProfileTransactionsLoading(true);
    }
    setProfileTransactionsError('');

    try {
      const response = await adminApi.getTransactions(token, buildTransactionsRequestDto(appliedProfileTransactionFilters.type, {
        limit: PROFILE_TRANSACTIONS_PAGE_SIZE,
        offset,
      }, {
        ...appliedProfileTransactionFilters,
        profile: profileId,
        ...profileTransactionSort,
      }));
      const rows = buildTransactionsTableModel(mapTransactionsResponseToDto(response));

      setProfileTransactionsRows((prev) => (append ? [...prev, ...rows] : rows));
      setHasMoreProfileTransactions(rows.length === PROFILE_TRANSACTIONS_PAGE_SIZE);
      setProfileTransactionsLoaded(true);
    } catch (requestError) {
      if (!append) {
        setProfileTransactionsRows([]);
        setHasMoreProfileTransactions(false);
      }
      setProfileTransactionsError(requestError.message || 'Failed to load profile transactions');
    } finally {
      if (append) {
        setIsProfileTransactionsLoadingMore(false);
      } else {
        setIsProfileTransactionsLoading(false);
      }
    }
  }, [appliedProfileTransactionFilters, currentProfileId, profileTransactionSort, token]);

  useEffect(() => {
    if (activeTab !== PROFILE_DETAILS_TABS.transactions || profileTransactionsLoaded || !id) {
      return undefined;
    }

    loadProfileTransactions().catch(() => {});
    return undefined;
  }, [activeTab, id, loadProfileTransactions, profileTransactionsLoaded]);

  const updateProfileTransactionFilter = useCallback((eventOrName, value) => {
    const field = typeof eventOrName === 'string'
      ? eventOrName
      : eventOrName?.target?.name;
    if (!['type', 'method', 'status'].includes(field)) {
      return;
    }

    const nextValue = typeof eventOrName === 'string'
      ? value
      : eventOrName?.target?.value;
    setProfileTransactionFilters((current) => normalizeProfileTransactionFilters({
      ...current,
      [field]: nextValue,
    }));
  }, []);

  const applyProfileTransactionFilters = useCallback(() => {
    setAppliedProfileTransactionFilters(normalizeProfileTransactionFilters(profileTransactionFilters));
    setProfileTransactionsRows([]);
    setHasMoreProfileTransactions(false);
    setProfileTransactionsLoaded(false);
    setProfileTransactionsError('');
  }, [profileTransactionFilters]);

  const clearProfileTransactionFilters = useCallback(() => {
    setProfileTransactionFilters(EMPTY_PROFILE_TRANSACTION_FILTERS);
    setAppliedProfileTransactionFilters(EMPTY_PROFILE_TRANSACTION_FILTERS);
    setProfileTransactionsRows([]);
    setHasMoreProfileTransactions(false);
    setProfileTransactionsLoaded(false);
    setProfileTransactionsError('');
  }, []);

  const changeProfileTransactionSort = useCallback((field) => {
    const normalizedField = String(field || '').trim();
    if (!['created', 'amount'].includes(normalizedField)) {
      return;
    }

    setProfileTransactionSort((current) => ({
      sortBy: normalizedField,
      sortOrder: current.sortBy === normalizedField && current.sortOrder === 'asc' ? 'desc' : 'asc',
    }));
    setProfileTransactionsRows([]);
    setHasMoreProfileTransactions(false);
    setProfileTransactionsLoaded(false);
    setProfileTransactionsError('');
  }, []);

  const loadMoreProfileTransactions = useCallback(() => {
    if (isProfileTransactionsLoading || isProfileTransactionsLoadingMore || !hasMoreProfileTransactions) {
      return;
    }

    loadProfileTransactions({
      append: true,
      offset: profileTransactionsRows.length,
    }).catch(() => {});
  }, [
    hasMoreProfileTransactions,
    isProfileTransactionsLoading,
    isProfileTransactionsLoadingMore,
    loadProfileTransactions,
    profileTransactionsRows.length,
  ]);

  const tabs = useMemo(() => buildProfileDetailsTabs(), []);

  const refreshProfileMail = useCallback(() => {
    setProfileMailLoaded(false);
  }, []);

  const updateProfileEditField = useCallback((field, value) => {
    setProfileEditForm((currentForm) => ({
      ...currentForm,
      [field]: String(value ?? ''),
    }));
    setProfileEditErrors((currentErrors) => {
      if (!currentErrors[field]) {
        return currentErrors;
      }

      const nextErrors = { ...currentErrors };
      delete nextErrors[field];
      return nextErrors;
    });
    setProfileSaveError('');
    setProfileSaveSuccess('');
  }, []);

  const resetProfileEditForm = useCallback(() => {
    setProfileEditForm(profileEditOriginal);
    setProfileEditErrors({});
    setProfileSaveError('');
    setProfileSaveSuccess('');
  }, [profileEditOriginal]);

  const resetProfileEmailErrors = useCallback(() => {
    setProfileEmailErrors({});
    setProfileSaveError('');
    setProfileSaveSuccess('');
  }, []);

  const saveProfileDetails = useCallback(async () => {
    const profileId = currentProfileId;
    if (!canUpdateProfile) {
      setProfileSaveSuccess('');
      setProfileSaveError(PROFILE_UPDATE_PERMISSION_ERROR);
      return false;
    }

    if (!profileId) {
      return false;
    }

    const payload = buildProfileUpdatePayload(profileEditOriginal, profileEditForm);
    if (Object.keys(payload).length === 0) {
      setProfileSaveError('');
      setProfileSaveSuccess('');
      return false;
    }

    setIsProfileSaving(true);
    setProfileEditErrors({});
    setProfileSaveError('');
    setProfileSaveSuccess('');
    const requestId = beginProfileEditSaveRequest();

    try {
      const response = await adminApi.updateProfile(token, profileId, payload);
      if (!isCurrentProfileEditSaveRequest(requestId, profileId)) {
        return false;
      }
      applyProfileResponse(response);
      setProfileSaveSuccess('Profile details updated.');
      return true;
    } catch (requestError) {
      if (!isCurrentProfileEditSaveRequest(requestId, profileId)) {
        return false;
      }
      setProfileSaveError(requestError.message || 'Failed to update profile details');
      return false;
    } finally {
      if (isCurrentProfileEditSaveRequest(requestId, profileId)) {
        setIsProfileSaving(false);
      }
    }
  }, [
    applyProfileResponse,
    beginProfileEditSaveRequest,
    canUpdateProfile,
    currentProfileId,
    isCurrentProfileEditSaveRequest,
    profileEditForm,
    profileEditOriginal,
    token,
  ]);

  const saveProfileEmail = useCallback(async (form) => {
    if (!canUpdateProfile) {
      setProfileEmailErrors({ form: PROFILE_UPDATE_PERMISSION_ERROR });
      setProfileSaveSuccess('');
      setProfileSaveError(PROFILE_UPDATE_PERMISSION_ERROR);
      return false;
    }

    const nextForm = {
      email: String(form?.email || '').trim(),
      reason: String(form?.reason || '').trim(),
    };
    const errors = validateEmailChangeForm(nextForm);
    setProfileEmailErrors(errors);
    setProfileSaveError('');
    setProfileSaveSuccess('');

    if (Object.keys(errors).length > 0) {
      return false;
    }

    const profileId = currentProfileId;
    if (!profileId) {
      return false;
    }

    setIsProfileEmailSaving(true);
    const requestId = beginProfileEditSaveRequest();

    try {
      const response = await adminApi.updateProfileEmail(token, profileId, nextForm);
      if (!isCurrentProfileEditSaveRequest(requestId, profileId)) {
        return false;
      }
      applyProfileResponse(response);
      setProfileEmailErrors({});
      setProfileSaveSuccess('Profile email updated.');
      return true;
    } catch (requestError) {
      if (!isCurrentProfileEditSaveRequest(requestId, profileId)) {
        return false;
      }
      const errorMessage = requestError.message || 'Failed to update profile email';
      setProfileEmailErrors({
        form: errorMessage,
      });
      setProfileSaveError(errorMessage);
      return false;
    } finally {
      if (isCurrentProfileEditSaveRequest(requestId, profileId)) {
        setIsProfileEmailSaving(false);
      }
    }
  }, [
    applyProfileResponse,
    beginProfileEditSaveRequest,
    canUpdateProfile,
    currentProfileId,
    isCurrentProfileEditSaveRequest,
    token,
  ]);

  const revealProfileEmail = useCallback(async (form) => {
    if (!canRevealProfilePii) {
      setProfileEmailRevealErrors({ form: PROFILE_PII_VIEW_PERMISSION_ERROR });
      setProfileSaveSuccess('');
      setProfileSaveError(PROFILE_PII_VIEW_PERMISSION_ERROR);
      return false;
    }

    const reason = String(form?.reason || '').trim();
    const errors = {};
    if (!reason) {
      errors.reason = 'Reason is required.';
    } else if (reason.length > 512) {
      errors.reason = 'Reason must be 512 characters or fewer.';
    }

    setProfileEmailRevealErrors(errors);
    setProfileSaveError('');
    setProfileSaveSuccess('');

    if (Object.keys(errors).length > 0) {
      return false;
    }

    const profileId = currentProfileId;
    if (!profileId) {
      return false;
    }

    setIsProfileEmailRevealSaving(true);
    const requestId = beginProfileEditSaveRequest();
    const securityRequestId = createSecurityRequestId();
    const payload = {
      reason,
      uiContext: 'admin-profile-details',
    };

    try {
      const response = await adminApi.revealProfileEmail(token, profileId, payload, {
        requestId: securityRequestId,
      });
      if (!isCurrentProfileEditSaveRequest(requestId, profileId)) {
        return false;
      }
      setRevealedEmail(String(response?.value || '').trim());
      setProfileEmailRevealErrors({});
      setProfileSaveSuccess('Profile email revealed.');
      return true;
    } catch (requestError) {
      if (!isCurrentProfileEditSaveRequest(requestId, profileId)) {
        return false;
      }
      if (isSecurityStepUpRequiredError(requestError)) {
        openSecurityStepUp({
          requestId: securityRequestId,
          retry: async () => {
            setIsProfileEmailRevealSaving(true);
            try {
              const response = await adminApi.revealProfileEmail(token, profileId, payload, {
                requestId: securityRequestId,
              });
              if (!isCurrentProfileEditSaveRequest(requestId, profileId)) {
                return false;
              }
              setRevealedEmail(String(response?.value || '').trim());
              setProfileEmailRevealErrors({});
              setProfileSaveSuccess('Profile email revealed.');
              setProfileSaveError('');
              return true;
            } finally {
              if (isCurrentProfileEditSaveRequest(requestId, profileId)) {
                setIsProfileEmailRevealSaving(false);
              }
            }
          },
        });
        return false;
      }
      const errorMessage = isSensitiveActionRestrictedError(requestError)
        ? requestError.message || SENSITIVE_ACTION_RESTRICTED_MESSAGE
        : requestError.message || 'Failed to reveal profile email';
      setProfileEmailRevealErrors({
        form: errorMessage,
      });
      setProfileSaveError(errorMessage);
      return false;
    } finally {
      if (isCurrentProfileEditSaveRequest(requestId, profileId)) {
        setIsProfileEmailRevealSaving(false);
      }
    }
  }, [
    beginProfileEditSaveRequest,
    canRevealProfilePii,
    currentProfileId,
    isCurrentProfileEditSaveRequest,
    openSecurityStepUp,
    token,
  ]);

  const revealProfilePhone = useCallback(async (form) => {
    if (!canRevealProfilePii) {
      setProfilePhoneRevealErrors({ form: PROFILE_PHONE_PII_VIEW_PERMISSION_ERROR });
      setProfileSaveSuccess('');
      setProfileSaveError(PROFILE_PHONE_PII_VIEW_PERMISSION_ERROR);
      return false;
    }

    const reason = String(form?.reason || '').trim();
    const errors = {};
    if (!reason) {
      errors.reason = 'Reason is required.';
    } else if (reason.length > 512) {
      errors.reason = 'Reason must be 512 characters or fewer.';
    }

    setProfilePhoneRevealErrors(errors);
    setProfileSaveError('');
    setProfileSaveSuccess('');

    if (Object.keys(errors).length > 0) {
      return false;
    }

    const profileId = currentProfileId;
    if (!profileId) {
      return false;
    }

    setIsProfilePhoneRevealSaving(true);
    const requestId = beginProfileEditSaveRequest();
    const securityRequestId = createSecurityRequestId();
    const payload = {
      reason,
      uiContext: 'admin-profile-details',
    };

    try {
      const response = await adminApi.revealProfilePhone(token, profileId, payload, {
        requestId: securityRequestId,
      });
      if (!isCurrentProfileEditSaveRequest(requestId, profileId)) {
        return false;
      }
      setRevealedPhone(String(response?.value || '').trim());
      setProfilePhoneRevealErrors({});
      setProfileSaveSuccess('Profile phone revealed.');
      return true;
    } catch (requestError) {
      if (!isCurrentProfileEditSaveRequest(requestId, profileId)) {
        return false;
      }
      if (isSecurityStepUpRequiredError(requestError)) {
        openSecurityStepUp({
          requestId: securityRequestId,
          retry: async () => {
            setIsProfilePhoneRevealSaving(true);
            try {
              const response = await adminApi.revealProfilePhone(token, profileId, payload, {
                requestId: securityRequestId,
              });
              if (!isCurrentProfileEditSaveRequest(requestId, profileId)) {
                return false;
              }
              setRevealedPhone(String(response?.value || '').trim());
              setProfilePhoneRevealErrors({});
              setProfileSaveSuccess('Profile phone revealed.');
              setProfileSaveError('');
              return true;
            } finally {
              if (isCurrentProfileEditSaveRequest(requestId, profileId)) {
                setIsProfilePhoneRevealSaving(false);
              }
            }
          },
        });
        return false;
      }
      const errorMessage = isSensitiveActionRestrictedError(requestError)
        ? requestError.message || SENSITIVE_ACTION_RESTRICTED_MESSAGE
        : requestError.message || 'Failed to reveal profile phone';
      setProfilePhoneRevealErrors({
        form: errorMessage,
      });
      setProfileSaveError(errorMessage);
      return false;
    } finally {
      if (isCurrentProfileEditSaveRequest(requestId, profileId)) {
        setIsProfilePhoneRevealSaving(false);
      }
    }
  }, [
    beginProfileEditSaveRequest,
    canRevealProfilePii,
    currentProfileId,
    isCurrentProfileEditSaveRequest,
    openSecurityStepUp,
    token,
  ]);

  const refreshPartnership = useCallback(() => {
    setPartnershipLoaded(false);
  }, []);

  const refreshSuitability = useCallback(() => {
    setSuitabilityLoaded(false);
  }, []);

  const closePartnershipModal = useCallback(() => {
    setIsPartnershipModalOpen(false);
    setPartnershipModalAccount(null);
    setPartnershipPartnerQuery('');
    setPartnershipSearchTouched(false);
    setPartnershipSearchResults([]);
    setIsPartnershipSearchLoading(false);
    setPartnershipSearchError('');
    setSelectedPartnershipPartner(null);
    setIsPartnershipSaving(false);
    setPartnershipSaveError('');
  }, []);

  const openPartnershipModal = useCallback((account) => {
    const currentPartner = account?.currentPartner || null;
    setPartnershipModalAccount(account || null);
    setIsPartnershipModalOpen(true);
    setPartnershipPartnerQuery(buildPartnerDisplay(currentPartner));
    setPartnershipSearchTouched(false);
    setPartnershipSearchResults([]);
    setIsPartnershipSearchLoading(false);
    setPartnershipSearchError('');
    setSelectedPartnershipPartner(currentPartner ? {
      uid: String(pickFirst(currentPartner.uid) || '').trim(),
      name: pickFirst(currentPartner.name) || '-',
      email: pickFirst(currentPartner.email) || '-',
      display: buildPartnerDisplay(currentPartner),
    } : null);
    setIsPartnershipSaving(false);
    setPartnershipSaveError('');
  }, []);

  const setPartnershipSearchQuery = useCallback((value) => {
    setPartnershipPartnerQuery(String(value ?? ''));
    setPartnershipSearchTouched(true);
    setSelectedPartnershipPartner(null);
    setPartnershipSaveError('');
  }, []);

  const selectPartnershipPartner = useCallback((partner) => {
    const normalizedPartner = partner ? {
      uid: String(pickFirst(partner.uid) || '').trim(),
      name: pickFirst(partner.name) || '-',
      email: pickFirst(partner.email) || '-',
      display: pickFirst(partner.display) || buildPartnerDisplay(partner),
    } : null;

    setSelectedPartnershipPartner(normalizedPartner);
    setPartnershipPartnerQuery(normalizedPartner?.display || '');
    setPartnershipSearchTouched(false);
    setPartnershipSearchResults([]);
    setPartnershipSearchError('');
    setPartnershipSaveError('');
  }, []);

  const savePartnershipPartner = useCallback(async () => {
    const accountUid = String(pickFirst(partnershipModalAccount?.uid, partnershipModalAccount?.id) || '').trim();
    const partnerUid = String(pickFirst(selectedPartnershipPartner?.uid) || '').trim();

    if (!accountUid) {
      setPartnershipSaveError('Missing account id');
      return;
    }

    if (!partnerUid) {
      setPartnershipSaveError('Please select a partner');
      return;
    }

    setIsPartnershipSaving(true);
    setPartnershipSaveError('');

    try {
      await adminApi.updateAccountPartner(token, accountUid, { partnerUid });
      closePartnershipModal();
      refreshPartnership();
    } catch (requestError) {
      setPartnershipSaveError(requestError.message || 'Failed to update partner');
    } finally {
      setIsPartnershipSaving(false);
    }
  }, [closePartnershipModal, partnershipModalAccount, refreshPartnership, selectedPartnershipPartner, token]);

  const unlinkPartnershipPartner = useCallback(async () => {
    const accountUid = String(pickFirst(partnershipModalAccount?.uid, partnershipModalAccount?.id) || '').trim();

    if (!accountUid) {
      setPartnershipSaveError('Missing account id');
      return;
    }

    setIsPartnershipSaving(true);
    setPartnershipSaveError('');

    try {
      await adminApi.updateAccountPartner(token, accountUid, { partnerUid: null });
      closePartnershipModal();
      refreshPartnership();
    } catch (requestError) {
      setPartnershipSaveError(requestError.message || 'Failed to unlink partner');
    } finally {
      setIsPartnershipSaving(false);
    }
  }, [closePartnershipModal, partnershipModalAccount, refreshPartnership, token]);

  const syncAccount = useCallback(
    async (account) => {
      const accountId = String(pickFirst(account?.id, account?.uid, account?.accountId) || '').trim();
      if (!accountId) {
        setAccountSyncSuccess('');
        setAccountSyncError('Failed to sync account: missing account id');
        return;
      }

      setSyncingAccountId(accountId);
      setAccountSyncError('');
      setAccountSyncSuccess('');

      try {
        const response = await adminApi.syncAccount(token, accountId);
        setAccountSyncSuccess(buildSyncSuccessMessage(response));
      } catch (requestError) {
        setAccountSyncError(requestError.message || 'Failed to sync account');
      } finally {
        setSyncingAccountId('');
      }
    },
    [token],
  );

  const reloadVerificationProfile = useCallback(async () => {
    if (!id || !canViewExternalVerifications) return;
    try {
      const response = await adminApi.getProfile(token, id);
      if (mountedRef.current && currentProfileIdRef.current === id) {
        setProfile(buildProfileDetailsModel(mapProfileResponseToDto(response)));
      }
    } catch (failure) {
      if (mountedRef.current && currentProfileIdRef.current === id) {
        setVerifyProfileError(failure.message || 'Could not reload verified profile.');
      }
    }
  }, [id, token, canViewExternalVerifications]);

  const verifyProfile = async () => {
    if (!id) {
      return;
    }

    setIsVerifyingProfile(true);
    setVerifyProfileError('');
    setVerifyProfileSuccess('');

    try {
      const response = await adminApi.updateProfileStatus(token, id, 'verified');
      const dto = mapProfileResponseToDto(response);
      setProfile(buildProfileDetailsModel(dto));
      setVerifyProfileSuccess('Profile status updated to verified');
    } catch (requestError) {
      setVerifyProfileError(requestError.message || 'Failed to verify profile');
    } finally {
      setIsVerifyingProfile(false);
    }
  };

  const requestProfileInformation = async () => {
    if (!id) {
      return;
    }

    const isConfirmed = window.confirm('Request additional information and set profile status to pending?');
    if (!isConfirmed) {
      return;
    }

    setIsRequestingInformation(true);
    setVerifyProfileError('');
    setVerifyProfileSuccess('');

    try {
      const response = await adminApi.updateProfileStatus(token, id, 'pending');
      const dto = mapProfileResponseToDto(response);
      setProfile(buildProfileDetailsModel(dto));
      setVerifyProfileSuccess('Profile status updated to pending');
    } catch (requestError) {
      setVerifyProfileError(requestError.message || 'Failed to request information');
    } finally {
      setIsRequestingInformation(false);
    }
  };

  const invalidateProfile = async (reason) => {
    if (!id) {
      return;
    }

    setIsInvalidatingProfile(true);
    setVerifyProfileError('');
    setVerifyProfileSuccess('');

    try {
      const response = await adminApi.invalidateProfile(token, id, { reason });
      const dto = mapProfileResponseToDto(response);
      setProfile(buildProfileDetailsModel(dto));
      setVerifyProfileSuccess('Profile invalidated');
    } catch (requestError) {
      setVerifyProfileError(requestError.message || 'Failed to invalidate profile');
    } finally {
      setIsInvalidatingProfile(false);
    }
  };

  const validateProfile = async (reason) => {
    if (!id) {
      return;
    }

    setIsValidatingProfile(true);
    setVerifyProfileError('');
    setVerifyProfileSuccess('');

    try {
      const response = await adminApi.validateProfile(token, id, { reason });
      const dto = mapProfileResponseToDto(response);
      setProfile(buildProfileDetailsModel(dto));
      setVerifyProfileSuccess('Profile validated');
    } catch (requestError) {
      setVerifyProfileError(requestError.message || 'Failed to validate profile');
    } finally {
      setIsValidatingProfile(false);
    }
  };

  const exportProfileData = useCallback(async () => {
    if (!id) {
      return;
    }

    const securityRequestId = createSecurityRequestId();

    setIsExportingProfile(true);
    setComplianceError('');
    setComplianceSuccess('');

    try {
      const response = await adminApi.exportProfile(token, id, {
        requestId: securityRequestId,
      });
      downloadProfileExportPayload(id, response);
      setComplianceSuccess('Profile export downloaded.');
    } catch (requestError) {
      if (isSecurityStepUpRequiredError(requestError)) {
        openSecurityStepUp({
          requestId: securityRequestId,
          retry: async () => {
            setIsExportingProfile(true);
            setComplianceError('');
            setComplianceSuccess('');
            try {
              const response = await adminApi.exportProfile(token, id, {
                requestId: securityRequestId,
              });
              downloadProfileExportPayload(id, response);
              setComplianceSuccess('Profile export downloaded.');
              return true;
            } finally {
              setIsExportingProfile(false);
            }
          },
        });
        return;
      }
      setComplianceError(
        isSensitiveActionRestrictedError(requestError)
          ? requestError.message || SENSITIVE_ACTION_RESTRICTED_MESSAGE
          : requestError.message || 'Failed to export profile data',
      );
    } finally {
      setIsExportingProfile(false);
    }
  }, [id, openSecurityStepUp, token]);

  const anonymizeProfile = useCallback(async () => {
    if (!id) {
      return;
    }

    const isConfirmed = window.confirm('Anonymize this profile and remove personal data?');
    if (!isConfirmed) {
      return;
    }

    setIsAnonymizingProfile(true);
    setComplianceError('');
    setComplianceSuccess('');

    try {
      const response = await adminApi.anonymizeProfile(token, id);
      setProfile((prev) => {
        if (!prev) {
          return prev;
        }

        const dto = mapProfileResponseToDto({
          ...(prev.source || {}),
          status: response?.profileStatus ?? prev.status,
          closureRequestedAt: response?.closureRequestedAt ?? prev.closureRequestedAt ?? null,
          consentWithdrawnAt: response?.consentWithdrawnAt ?? prev.consentWithdrawnAt ?? null,
          legalHoldUntil: response?.legalHoldUntil ?? prev.legalHoldUntil ?? null,
        });

        return buildProfileDetailsModel(dto);
      });
      setComplianceSuccess('Profile anonymized.');
    } catch (requestError) {
      setComplianceError(requestError.message || 'Failed to anonymize profile');
    } finally {
      setIsAnonymizingProfile(false);
    }
  }, [id, token]);

  const updateVulnerability = useCallback(async (payload) => {
    if (!id) {
      return;
    }

    setIsUpdatingVulnerability(true);
    setComplianceError('');
    setComplianceSuccess('');

    try {
      const response = await adminApi.updateProfileVulnerability(token, id, payload);
      setProfile((prev) => {
        if (!prev) {
          return prev;
        }

        const nextSource = {
          ...(prev.source || {}),
          vulnerabilityFlag: response?.flag ?? null,
          vulnerabilityNotes: response?.notes ?? null,
        };

        return buildProfileDetailsModel(mapProfileResponseToDto(nextSource));
      });
      setComplianceSuccess('Vulnerability status updated.');
    } catch (requestError) {
      setComplianceError(requestError.message || 'Failed to update vulnerability status');
      throw requestError;
    } finally {
      setIsUpdatingVulnerability(false);
    }
  }, [id, token]);

  const createSuitabilityAssessment = useCallback(async (payload) => {
    if (!id) {
      return;
    }

    setIsCreatingSuitability(true);
    setComplianceError('');
    setComplianceSuccess('');

    try {
      await adminApi.createProfileSuitability(token, id, payload);
      refreshSuitability();
      setComplianceSuccess('Suitability assessment recorded.');
    } catch (requestError) {
      setComplianceError(requestError.message || 'Failed to create suitability assessment');
      throw requestError;
    } finally {
      setIsCreatingSuitability(false);
    }
  }, [id, refreshSuitability, token]);

  const checkAuditIntegrity = useCallback(async () => {
    if (!id) {
      return;
    }

    setIsAuditChecking(true);
    setComplianceError('');
    setComplianceSuccess('');

    try {
      const response = await adminApi.getProfileAuditIntegrity(token, id);
      setAuditIntegrity(response);
      setComplianceSuccess(response?.isValid ? 'Audit log integrity verified.' : 'Audit log integrity check found mismatches.');
    } catch (requestError) {
      setComplianceError(requestError.message || 'Failed to check audit integrity');
      throw requestError;
    } finally {
      setIsAuditChecking(false);
    }
  }, [id, token]);

  return {
    token,
    profileId: id,
    tabs,
    activeTab,
    setActiveTab,
    profile,
    canUpdateProfile,
    canViewExternalVerifications,
    reloadVerificationProfile,
    canRevealProfilePii,
    profileEditForm,
    profileEditErrors,
    isProfileSaving,
    profileSaveError,
    profileSaveSuccess,
    profileEmailErrors,
    isProfileEmailSaving,
    profileEmailRevealErrors,
    isProfileEmailRevealSaving,
    revealedEmail,
    profilePhoneRevealErrors,
    isProfilePhoneRevealSaving,
    revealedPhone,
    updateProfileEditField,
    resetProfileEditForm,
    resetProfileEmailErrors,
    saveProfileDetails,
    saveProfileEmail,
    revealProfileEmail,
    revealProfilePhone,
    isLoading,
    error,
    accounts,
    isAccountsLoading,
    accountsError,
    profileTransactionsRows,
    isProfileTransactionsLoading,
    isProfileTransactionsLoadingMore,
    hasMoreProfileTransactions,
    profileTransactionsError,
    profileTransactionFilters,
    profileTransactionStatusOptions: PROFILE_TRANSACTION_STATUS_OPTIONS,
    profileTransactionSort,
    updateProfileTransactionFilter,
    applyProfileTransactionFilters,
    clearProfileTransactionFilters,
    changeProfileTransactionSort,
    loadMoreProfileTransactions,
    partnership,
    isPartnershipLoading,
    partnershipError,
    isPartnershipLoaded: partnershipLoaded,
    isPartnershipModalOpen,
    partnershipModalAccount,
    partnershipPartnerQuery,
    setPartnershipPartnerQuery: setPartnershipSearchQuery,
    partnershipSearchResults,
    isPartnershipSearchLoading,
    partnershipSearchError,
    selectedPartnershipPartner,
    isPartnershipSaving,
    partnershipSaveError,
    openPartnershipModal,
    closePartnershipModal,
    selectPartnershipPartner,
    savePartnershipPartner,
    unlinkPartnershipPartner,
    syncingAccountId,
    accountSyncError,
    accountSyncSuccess,
    syncAccount,
    kyc,
    isKycLoading,
    kycError,
    kycSettings,
    isKycSettingsLoading,
    kycSettingsError,
    documents,
    isDocumentsLoading,
    documentsError,
    refreshProfileDocuments,
    actionLogs,
    isActionLogsLoading,
    actionLogsError,
    profileMail,
    isProfileMailLoading,
    profileMailError,
    refreshProfileMail,
    suitabilityAssessments,
    isSuitabilityLoading,
    suitabilityError,
    refreshSuitability,
    isCreatingSuitability,
    isUpdatingVulnerability,
    updateVulnerability,
    createSuitabilityAssessment,
    auditIntegrity,
    isAuditChecking,
    checkAuditIntegrity,
    isVerifyingProfile,
    isRequestingInformation,
    isInvalidatingProfile,
    isValidatingProfile,
    verifyProfileError,
    verifyProfileSuccess,
    verifyProfile,
    requestProfileInformation,
    invalidateProfile,
    validateProfile,
    isAnonymizingProfile,
    isExportingProfile,
    complianceError,
    complianceSuccess,
    anonymizeProfile,
    exportProfileData,
    securityStepUp,
    submitSecurityStepUpCode,
    cancelSecurityStepUp,
    trades,
    tradesTotal,
    tradesTotalLots,
    tradesTotalProfit,
    tradesProfitCurrency,
    isTradesLoading,
    tradesError,
    tradesSymbolFilter,
    tradesDateFromFilter,
    tradesDateToFilter,
    handleTradesSymbolChange,
    handleTradesDateFromChange,
    handleTradesDateToChange,
    loadMoreTrades,
  };
}
